// wordHighlight.js — Wort-Box-Heuristiken fuer den Mapping-Editor: findet zu einer Regelset-
// SPEZIFIKATION (x-Band, Regex-Muster) die zugehoerigen OCR-Wort-Boxen im Dokument (fuer die
// Fundstellen-Anzeige im AbPdfViewer). Port der lokalen Helfer aus OC-Pilot MappingView.tsx
// (columnBoxes/regexWordBoxes/fieldHighlight/escapeRe), NUR gelesen unter
// scratchpad/OC-Pilot/frontend/src/pages/MappingView.tsx.
//
// Bewusst NICHT identisch mit components/pdf/wordMatch.js (FE-2, Welle 1): wordMatch.js sucht
// Fundstellen zu einem bereits EXTRAHIERTEN WERT (Detail-Ansicht), dieses Modul sucht Fundstellen
// zu einer REGELSET-SPEZIFIKATION (x-Band/Regex-Muster) waehrend der Bearbeitung im Mapping-
// Editor — unterschiedlicher Zweck, daher eigenes, kleines Modul statt Zweckentfremdung.
//
// Reine Funktionen ohne React; Eingabe `words` = WordsResponse aus GET /documents/:id/words
// ({ pages: [{ page, width, height, words: [{ x0, y0, x1, y1, t }] }] }).

const r1 = (n) => Math.round(n * 10) / 10;

/** Regex-Sonderzeichen escapen (fuer Text-Muster-Vorschlaege aus PDF-Klicks). */
export function escapeRegex(s) {
  return String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** x-Band (xMin/xMax) aus einer angeklickten Wort-Box ableiten, gepolstert um bandPad. */
export function bandFromWord(word, bandPad = 3) {
  return { xMin: r1(word.x0 - bandPad), xMax: r1(word.x1 + bandPad) };
}

/** Woerter einer Seite, deren Mittelpunkt in der x-Spalte [xMin, xMax] liegt. */
export function columnBoxes(words, page, xMin, xMax) {
  const pg = words?.pages?.find((p) => p.page === page);
  if (!pg || xMin == null || xMax == null) return [];
  return pg.words
    .filter((w) => { const c = (w.x0 + w.x1) / 2; return c >= xMin && c <= xMax; })
    .map((w) => ({ x0: w.x0, y0: w.y0, x1: w.x1, y1: w.y1 }));
}

/**
 * Regex auf dem (aus Woertern je Seite rekonstruierten) Seitentext -> Boxen der getroffenen
 * Gruppe (Best-Effort, erste treffende Seite gewinnt). Erfordert Browser-Unterstuetzung fuer das
 * Regex-Flag 'd' (Match-Indices, seit Chrome 90/Firefox 88/Safari 15 — im Ziel-Browserkreis der
 * Suite gegeben); ohne 'd'-Unterstuetzung liefert die Funktion defensiv null (kein Crash).
 */
export function regexWordBoxes(words, pattern, group = 1) {
  if (!words || !pattern) return null;
  let re;
  try { re = new RegExp(pattern, 'd'); } catch { return null; }
  for (const pg of words.pages || []) {
    const sorted = [...pg.words].sort((a, b) => (Math.round(a.y0) - Math.round(b.y0)) || (a.x0 - b.x0));
    let text = '';
    const spans = [];
    sorted.forEach((w, i) => {
      if (i) text += ' ';
      const start = text.length;
      text += w.t;
      spans.push({ start, end: text.length, w });
    });
    let m;
    try { m = re.exec(text); } catch { return null; }
    if (!m) continue;
    const idx = m.indices?.[group] ?? m.indices?.[0] ?? [m.index, m.index + m[0].length];
    const boxes = spans.filter((s) => s.start < idx[1] && s.end > idx[0]).map((s) => ({ x0: s.w.x0, y0: s.w.y0, x1: s.w.x1, y1: s.w.y1 }));
    if (boxes.length) return { page: pg.page, boxes };
  }
  return null;
}

/**
 * Fundstelle(n) fuer ein Feld ermitteln (Quelle: Kopf-Bereich/Regex/fester Wert, Positionsspalte
 * [+ PE-Spalte bei price_per_unit], Positionsblock-Muster oder Lieferungs-Anker).
 * @returns {{page:number, boxes:Array}|null}
 */
export function fieldHighlight(words, form, fm) {
  if (!words) return null;
  if (fm.scope === 'header') {
    if (fm.headerMethod === 'box' && fm.box) return { page: fm.box.page, boxes: [fm.box] };
    if (fm.headerMethod === 'regex' && fm.regexPattern) return regexWordBoxes(words, fm.regexPattern, fm.group ?? 1);
    if (fm.headerMethod === 'presence' && fm.presenceValue) return regexWordBoxes(words, escapeRegex(fm.presenceValue), 0);
    return null;
  }
  if (fm.scope === 'line') {
    if (form.strategy === 'blocks') {
      const ppu = fm.format === 'price_per_unit' || fm.format === 'price_per_pe_code';
      const pat = ppu ? fm.ppuPattern : fm.linePattern;
      return pat ? regexWordBoxes(words, pat, ppu ? (fm.ppuPriceGroup ?? 1) : (fm.lineGroup ?? 1)) : null;
    }
    if (fm.lineMode === 'label') {
      return fm.linePattern ? regexWordBoxes(words, fm.linePattern, 1) : null;
    }
    const boxes = [];
    if (fm.xMin != null && fm.xMax != null) boxes.push(...columnBoxes(words, form.page, fm.xMin, fm.xMax));
    if (fm.format === 'price_per_unit' && fm.unitXMin != null && fm.unitXMax != null) boxes.push(...columnBoxes(words, form.page, fm.unitXMin, fm.unitXMax));
    return boxes.length ? { page: form.page, boxes } : null;
  }
  if (fm.scope === 'delivery' && form.deliveryAnchor) return regexWordBoxes(words, escapeRegex(form.deliveryAnchor), 0);
  return null;
}

/** Fundstelle des Positions-Ankers (Positionsspalte, Strategie column_rows) — fuer die "Positionen"-Tab-Vorschau. */
export function rowAnchorHighlight(words, form) {
  if (form.rowAnchorXMin == null || form.rowAnchorXMax == null) return null;
  const boxes = columnBoxes(words, form.page, form.rowAnchorXMin, form.rowAnchorXMax);
  return boxes.length ? { page: form.page, boxes } : null;
}
